import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
// ============================================
// KEYFRAME ANIMATIONS
// ============================================
const fadeUp = keyframes `
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;
// ============================================
// STYLED COMPONENTS
// ============================================
const FullPageContainer = styled.div `
  width: 100%;
  background: var(--bg-primary);
  min-height: 100vh;
`;
// Hero Section
const HeroSection = styled.section `
  position: relative;
  height: calc(100vh - 70px);
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;
const HeroVideo = styled.video `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  z-index: 0;

  @media (max-width: 480px) {
    display: none;
  }
`;
const HeroOverlay = styled.div `
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1;

  @media (max-width: 480px) {
    background: linear-gradient(135deg, rgba(20, 20, 25, 0.95) 0%, rgba(10, 10, 15, 0.98) 100%);
  }
`;
const HeroContent = styled.div `
  position: relative;
  z-index: 10;
  text-align: center;
  max-width: 700px;
  padding: 48px 56px;
  background: var(--bg-secondary);
  opacity: 0.95;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);

  @media (max-width: 768px) {
    margin: 20px;
    padding: 32px 24px;
  }
`;
const HeroEyebrow = styled.span `
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  color: var(--accent-gold);
  margin-bottom: 16px;
  opacity: 0;
  animation: ${fadeUp} 700ms ease-out 200ms forwards;
`;
const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif !important;
  font-size: 52px !important;
  font-weight: 500 !important;
  line-height: 1.15 !important;
  color: var(--text-primary) !important;
  margin: 0 0 20px 0 !important;
  opacity: 0;
  animation: ${fadeUp} 700ms ease-out 350ms forwards;

  @media (max-width: 768px) {
    font-size: 36px !important;
  }

  @media (max-width: 480px) {
    font-size: 28px !important;
  }
`;
const HeroSubtitle = styled.p `
  font-size: 18px !important;
  color: var(--text-secondary) !important;
  line-height: 1.7 !important;
  margin: 0 0 32px 0 !important;
  opacity: 0;
  animation: ${fadeUp} 700ms ease-out 500ms forwards;

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`;
const HeroButtons = styled.div `
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  opacity: 0;
  animation: ${fadeUp} 700ms ease-out 650ms forwards;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;
const GoldButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  font-family: 'DM Sans', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border-radius: 6px;
  border: 1px solid var(--accent-gold);
  cursor: pointer;
  transition: all 0.25s ease;
  background: var(--accent-gold);
  color: #FFFFFF;

  &:hover {
    background: var(--accent-gold-dark);
    border-color: var(--accent-gold-dark);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md), var(--shadow-glow);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;
const SecondaryButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 32px;
  font-family: 'DM Sans', 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.03em;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.25s ease;
  background: var(--bg-secondary);
  color: var(--text-primary);

  &:hover {
    border-color: var(--accent-gold);
    color: var(--accent-gold);
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    width: 100%;
    max-width: 280px;
  }
`;
// ============================================
// ICONS (inline SVG components)
// ============================================
const GemIcon = () => (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M6 3h12l4 6-10 13L2 9z" }), _jsx("path", { d: "M11 3 8 9l4 13 4-13-3-6" }), _jsx("path", { d: "M2 9h20" })] }));
// ============================================
// MAIN COMPONENT
// ============================================
const Home = () => {
    const navigate = useNavigate();
    return (_jsx(FullPageContainer, { children: _jsxs(HeroSection, { children: [_jsx(HeroVideo, { autoPlay: true, muted: true, loop: true, playsInline: true, preload: "metadata", children: _jsx("source", { src: "/hero-diamond-spin.mp4", type: "video/mp4" }) }), _jsx(HeroOverlay, {}), _jsxs(HeroContent, { children: [_jsx(HeroEyebrow, { children: "Established 1984" }), _jsxs(HeroTitle, { children: ["Mine to Market", _jsx("br", {}), "Traceability"] }), _jsx(HeroSubtitle, { children: "With over 40 years of global service, we present a new dimension in the diamond procurement process." }), _jsxs(HeroButtons, { children: [_jsxs(GoldButton, { onClick: () => navigate('/login'), children: [_jsx(GemIcon, {}), "Enter Platform"] }), _jsx(SecondaryButton, { onClick: () => navigate('/contact'), children: "Contact Us" })] })] })] }) }));
};
export default Home;
